import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { AlertTriangle, Clock } from 'lucide-react';
import type { MissedDepositRow } from '@/domain/missedDeposits';

/**
 * The sweeper's "missed deposits" morning list (bd tv-d2f, board 08): unpaid
 * gap-fill links past their 24h lock, the sweeper's first priority each morning.
 * Links unpaid past 48h are flagged as escalated to an Ops report. Chasing these
 * takes priority over fresh sweeping — the client already said yes to a slot.
 */
export default function MissedDepositsList({ rows }: { rows: MissedDepositRow[] }) {
  const navigate = useNavigate();

  if (rows.length === 0) {
    return (
      <div
        className="panel-dark p-6 text-center font-serif text-cream-soft max-w-2xl"
        data-testid="missed-deposits-empty"
      >
        No missed deposits. Every gap-fill link sent is either paid or still inside its 24h lock.
      </div>
    );
  }

  return (
    <div data-testid="missed-deposits-list">
      <p className="font-serif text-[13px] text-cream-soft mb-4 max-w-3xl leading-relaxed">
        Unpaid gap-fill links past their 24h lock — chase these first each morning; the client
        already said yes to a slot. Anything still unclosed past 48h is escalated to an Ops report.
      </p>
      <ul>
        {rows.map((row) => (
          <li
            key={row.depositId}
            className="flex items-center gap-4 border-t border-gold-dim/15 py-3.5 first:border-t-0"
            data-testid={`missed-deposit-${row.depositId}`}
          >
            <div
              className={clsxTone(row.escalated)}
              aria-hidden
            >
              {row.escalated ? <AlertTriangle size={17} /> : <Clock size={17} />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-inter text-[13.5px] text-cream font-semibold">{row.customerName}</p>
              <p className="font-serif text-[12.5px] text-cream-soft mt-0.5">
                ${row.amountAud.toLocaleString()} link sent {row.hoursSinceSent}h ago, unpaid
                {row.sessionIso ? ` · ${format(new Date(row.sessionIso), 'EEE d MMM')} slot` : ''}
              </p>
              {row.escalated ? (
                <span className="inline-block mt-1.5 rounded-full border border-blood/40 px-2 py-0.5 font-inter text-[10px] uppercase tracking-widest text-blood">
                  Escalated to Ops · 48h+ unclosed
                </span>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => (row.customer ? navigate(`/customer/${row.customer.id}`) : navigate('/deposits'))}
              className="shrink-0 rounded-md bg-gold-bright px-4 py-2 text-xs font-semibold text-ink-black transition hover:bg-gold"
              data-testid={`missed-deposit-call-${row.depositId}`}
            >
              Call back
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function clsxTone(escalated: boolean): string {
  return [
    'shrink-0 w-9 h-9 rounded-full grid place-items-center',
    escalated ? 'bg-blood/10 text-blood' : 'bg-gold/15 text-gold-bright',
  ].join(' ');
}
